/**
 * Admin audit log presentation: turns a raw audit row (action code, `entityType:entityId`,
 * before/after JSON) into what a reviewer reads — a plain label, a category and one sentence
 * naming who did what to whom. Pure, so the page and its tests share it.
 */

import type { ActionTone as AuditTone } from './auditActionChip';

export type { AuditTone };

export type AuditCategory = 'signin' | 'workstation' | 'users' | 'access' | 'loan_review' | 'services' | 'system';

export const AUDIT_CATEGORY_LABELS: Readonly<Record<AuditCategory, string>> = {
  signin: 'Sign-ins',
  workstation: 'Workstation security',
  users: 'Users & groups',
  access: 'Roles & access',
  loan_review: 'Loan review',
  services: 'Vendors & services',
  system: 'System',
};

type ActionMeta = {
  label: string;
  category: AuditCategory;
  tone: AuditTone;
  /** Routine machine records hidden unless the viewer asks for them. */
  routine?: boolean;
  /** "{actor} {verb} {subject}" for actions without a hand-written sentence. */
  verb?: string;
};

/** Every admin-api audit action (admin-api/src/domain/AdminAuditLog.ts) with its display metadata. */
export const AUDIT_ACTION_META = {
  CREATE_VENDOR: { label: 'Vendor created', category: 'services', tone: 'success', verb: 'created vendor' },
  UPDATE_VENDOR: { label: 'Vendor updated', category: 'services', tone: 'info', verb: 'updated vendor' },
  DELETE_VENDOR: { label: 'Vendor deleted', category: 'services', tone: 'error', verb: 'deleted vendor' },
  UPDATE_VENDOR_SERVICE_ROUTING: { label: 'Vendor routing changed', category: 'services', tone: 'info', verb: 'changed routing for' },
  SYNC_THIRD_PARTY_CONFIG: { label: 'Third-party config synced', category: 'services', tone: 'default', verb: 'synced third-party config for' },
  CREATE_ADMIN_USER: { label: 'User created', category: 'users', tone: 'success' },
  PROVISION_OPERATOR_IDEMPOTENT: {
    label: 'Sign-in account check',
    category: 'signin',
    tone: 'default',
    routine: true,
  },
  UPDATE_ADMIN_USER: { label: 'User updated', category: 'users', tone: 'info' },
  DELETE_ADMIN_USER: { label: 'User deleted', category: 'users', tone: 'error' },
  CREATE_GROUP: { label: 'Group created', category: 'users', tone: 'success', verb: 'created group' },
  UPDATE_GROUP: { label: 'Group updated', category: 'users', tone: 'info', verb: 'updated group' },
  DELETE_GROUP: { label: 'Group deleted', category: 'users', tone: 'error', verb: 'deleted group' },
  ASSIGN_USER_GROUP: { label: 'Added to group', category: 'users', tone: 'success', verb: 'added a group to' },
  UNASSIGN_USER_GROUP: { label: 'Removed from group', category: 'users', tone: 'error', verb: 'removed a group from' },
  IAM_PERMISSION_SYNCED: { label: 'Permission catalog synced', category: 'system', tone: 'default', routine: true },
  IAM_ROLE_CREATED: { label: 'Role created', category: 'access', tone: 'success', verb: 'created role' },
  IAM_ROLE_UPDATED: { label: 'Role updated', category: 'access', tone: 'info', verb: 'updated role' },
  IAM_ROLE_DELETED: { label: 'Role deleted', category: 'access', tone: 'error', verb: 'deleted role' },
  IAM_ROLE_ASSIGNED: { label: 'Role granted', category: 'access', tone: 'success', verb: 'granted a role:' },
  IAM_ROLE_UNASSIGNED: { label: 'Role revoked', category: 'access', tone: 'error', verb: 'revoked a role:' },
  IAM_ASSIGNMENT_UPDATED: { label: 'Role grant changed', category: 'access', tone: 'info', verb: 'changed a role grant:' },
  IAM_ASSIGNMENT_EXPIRED: { label: 'Role grant expired', category: 'access', tone: 'warning' },
  IAM_REVIEW_CREATED: { label: 'Access review started', category: 'access', tone: 'info', verb: 'started access review' },
  IAM_REVIEW_ITEM_DECIDED: { label: 'Access review decision', category: 'access', tone: 'info', verb: 'made a decision in access review' },
  IAM_REVIEW_ITEM_REASSIGNED: { label: 'Access review reassigned', category: 'access', tone: 'warning', verb: 'reassigned an item in access review' },
  IAM_REVIEW_CLOSED: { label: 'Access review closed', category: 'access', tone: 'default', verb: 'closed access review' },
  IAM_MFA_STEP_UP: { label: 'MFA confirmed', category: 'signin', tone: 'secondary' },
  IAM_ELIGIBILITY_CREATED: { label: 'Made eligible for role', category: 'access', tone: 'success', verb: 'made someone eligible:' },
  IAM_ELIGIBILITY_UPDATED: { label: 'Eligibility changed', category: 'access', tone: 'info', verb: 'changed an eligibility:' },
  IAM_ELIGIBILITY_DELETED: { label: 'Eligibility removed', category: 'access', tone: 'error', verb: 'removed an eligibility:' },
  IAM_ACTIVATION_REQUESTED: { label: 'Role activation requested', category: 'access', tone: 'info', verb: 'requested activation:' },
  IAM_ACTIVATION_APPROVED: { label: 'Role activation approved', category: 'access', tone: 'success', verb: 'approved activation:' },
  IAM_ACTIVATION_REJECTED: { label: 'Role activation rejected', category: 'access', tone: 'error', verb: 'rejected activation:' },
  IAM_ACTIVATION_CANCELLED: { label: 'Role activation cancelled', category: 'access', tone: 'default', verb: 'cancelled activation:' },
  IAM_ACTIVATION_ACTIVATED: { label: 'Role activated', category: 'access', tone: 'success', verb: 'activated:' },
  IAM_ACTIVATION_ENDED: { label: 'Role activation ended', category: 'access', tone: 'default', verb: 'ended activation:' },
  IDENTITY_LINKED: { label: 'SSO identity linked', category: 'signin', tone: 'secondary' },
  BREAK_GLASS_SIGN_IN: { label: 'Break-glass sign-in', category: 'signin', tone: 'error' },
  PASSKEY_ENROLMENT_TOKEN_ISSUED: { label: 'Passkey code issued', category: 'signin', tone: 'warning' },
  RESET_ADMIN_PASSWORD: { label: 'Password reset', category: 'users', tone: 'warning' },
  LOGIN_SUCCESS: { label: 'Signed in', category: 'signin', tone: 'secondary' },
  LOGIN_FAILURE: { label: 'Sign-in failed', category: 'signin', tone: 'error' },
  PUBLISH_SERVICE_CONFIG: { label: 'Service config published', category: 'services', tone: 'info', verb: 'published service config' },
  CREATE_SERVICE_REGISTRATION: { label: 'Service registered', category: 'services', tone: 'success', verb: 'registered service' },
  DELETE_SERVICE_REGISTRATION: { label: 'Service unregistered', category: 'services', tone: 'error', verb: 'unregistered service' },
  IMPORT_ENV_PROPOSAL: { label: 'Config import proposed', category: 'services', tone: 'info', verb: 'proposed a config import for' },
  DUPLICATE_SERVICE_REGISTRATION: { label: 'Service duplicated', category: 'services', tone: 'success', verb: 'duplicated service' },
  DELETE_SERVICE_CONFIG_GROUP: { label: 'Config group deleted', category: 'services', tone: 'error', verb: 'deleted config group' },
  WORKSTATION_LOCKED: { label: 'Workstation locked', category: 'workstation', tone: 'warning' },
  WORKSTATION_UNLOCKED: { label: 'Workstation unlocked', category: 'workstation', tone: 'success' },
  WORKSTATION_UNLOCK_REFUSED: { label: 'Unlock refused', category: 'workstation', tone: 'error' },
  FACE_ENROLLED: { label: 'Face enrolled', category: 'workstation', tone: 'info' },
  LOAN_REVIEW_SHIFT_STARTED: { label: 'Clocked on', category: 'loan_review', tone: 'success' },
  LOAN_REVIEW_SHIFT_ENDED: { label: 'Clocked off', category: 'loan_review', tone: 'default' },
  LOAN_REVIEW_SHIFT_ENDED_HOLDING: { label: 'Clocked off holding a file', category: 'loan_review', tone: 'warning' },
  LOAN_REVIEW_OVERDUE: { label: 'Review overdue', category: 'loan_review', tone: 'error' },
  LOAN_REVIEW_DECIDED: { label: 'Loan decided', category: 'loan_review', tone: 'info' },
} as const satisfies Record<string, ActionMeta>;

export type KnownAuditAction = keyof typeof AUDIT_ACTION_META;

export const AUDIT_ACTIONS = Object.keys(AUDIT_ACTION_META) as KnownAuditAction[];

/** Actions shown when the viewer has not picked any: everything except routine machine records. */
export const NON_ROUTINE_AUDIT_ACTIONS: readonly KnownAuditAction[] = AUDIT_ACTIONS.filter(
  (a) => !(AUDIT_ACTION_META[a] as ActionMeta).routine,
);

export function auditActionMeta(action: string): ActionMeta {
  const meta = (AUDIT_ACTION_META as Record<string, ActionMeta>)[action];
  if (meta) return meta;
  const words = action.toLowerCase().replace(/_/g, ' ');
  return { label: words.charAt(0).toUpperCase() + words.slice(1), category: 'system', tone: 'default' };
}

export function actionsInCategory(category: AuditCategory): KnownAuditAction[] {
  return AUDIT_ACTIONS.filter((a) => AUDIT_ACTION_META[a].category === category && !(AUDIT_ACTION_META[a] as ActionMeta).routine);
}

/** The category whose (non-routine) actions exactly match `actions`, if any — drives the active chip. */
export function categoryForActions(actions: readonly string[]): AuditCategory | null {
  if (actions.length === 0) return null;
  const want = [...new Set(actions)].sort().join(',');
  for (const c of Object.keys(AUDIT_CATEGORY_LABELS) as AuditCategory[]) {
    if (actionsInCategory(c).sort().join(',') === want) return c;
  }
  return null;
}

export type AuditRowLike = {
  action: string;
  actorEmail: string;
  actorUserId: string | null;
  entityType: string;
  entityId: string;
  beforeState: Record<string, unknown> | null;
  afterState: Record<string, unknown> | null;
};

/** Staff user id → email or display name, from the users list when the viewer may read it. */
export type StaffNameLookup = ReadonlyMap<string, string>;

const SYSTEM_ACTOR = /^system(:|$)/i;

export function isSystemActor(row: Pick<AuditRowLike, 'actorEmail' | 'actorUserId'>): boolean {
  return SYSTEM_ACTOR.test(row.actorEmail ?? '') || (!row.actorEmail && !row.actorUserId);
}

/** "System" for machine actors (`system:seed`, the expiry job), else the actor's email. */
export function auditActorLabel(row: Pick<AuditRowLike, 'actorEmail' | 'actorUserId'>, names?: StaffNameLookup): string {
  if (isSystemActor(row)) {
    const detail = /^system:(.+)$/i.exec(row.actorEmail ?? '')?.[1];
    return detail ? `System (${detail.replace(/[_-]/g, ' ')})` : 'System';
  }
  return row.actorEmail || (row.actorUserId && names?.get(row.actorUserId)) || shortId(row.actorUserId);
}

export function shortId(id: string | null | undefined): string {
  if (!id) return '—';
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

function field(row: AuditRowLike, ...keys: string[]): string | null {
  for (const state of [row.afterState, row.beforeState]) {
    if (!state) continue;
    for (const k of keys) {
      const v = str(state[k]);
      if (v) return v;
    }
  }
  return null;
}

/** The thing the event is about, by name where the row or the users list gives one. */
export function auditSubjectLabel(row: AuditRowLike, names?: StaffNameLookup): string {
  const id = row.entityId;
  switch (row.entityType) {
    case 'admin_user':
    case 'workstation':
    case 'loan_reviewer':
      return field(row, 'email', 'displayName') ?? names?.get(id) ?? `user ${shortId(id)}`;
    case 'iam_role':
      return field(row, 'name', 'key') ?? `role ${shortId(id)}`;
    case 'iam_permission':
      return field(row, 'label') ?? id;
    case 'admin_group':
      return field(row, 'name') ?? `group ${shortId(id)}`;
    case 'application':
      return `application ${shortId(field(row, 'application_id') ?? id)}`;
    case 'adapter_service':
      return field(row, 'adapterServiceKey', 'name') ?? id;
    default:
      return field(row, 'name', 'email', 'key') ?? `${row.entityType.replace(/_/g, ' ')} ${shortId(id)}`;
  }
}

/** "user x@y.z" — the id fallback already reads "user 1234…". */
function userNoun(subject: string): string {
  return subject.startsWith('user ') ? subject : `user ${subject}`;
}

/** True when the event is about the actor themself (their own sign-in, workstation, shift). */
function isSelf(row: AuditRowLike): boolean {
  if (!row.actorUserId) return false;
  return ['admin_user', 'workstation', 'loan_reviewer'].includes(row.entityType) && row.entityId === row.actorUserId;
}

/** One sentence: who did what to whom. */
export function auditSummary(row: AuditRowLike, names?: StaffNameLookup): string {
  const actor = auditActorLabel(row, names);
  const subject = auditSubjectLabel(row, names);
  const self = isSelf(row);
  const target = self ? 'their own account' : subject;
  const reviewer = field(row, 'reviewer_email', 'reviewer_display_name');

  switch (row.action) {
    case 'LOGIN_SUCCESS':
      return `${actor} signed in`;
    case 'LOGIN_FAILURE':
      return `Failed sign-in for ${subject}`;
    case 'PROVISION_OPERATOR_IDEMPOTENT':
      return `Sign-in check found the existing account for ${subject}`;
    case 'IAM_MFA_STEP_UP':
      return `${actor} confirmed MFA for this session`;
    case 'BREAK_GLASS_SIGN_IN':
      return `${actor} signed in with a break-glass account`;
    case 'IDENTITY_LINKED':
      return `${subject} was linked to their SSO identity`;
    case 'WORKSTATION_LOCKED':
      return `${self ? actor : subject}'s workstation was locked${field(row, 'reason') ? ` (${field(row, 'reason')!.replace(/_/g, ' ')})` : ''}`;
    case 'WORKSTATION_UNLOCKED':
      return `${self ? actor : subject} unlocked their workstation`;
    case 'WORKSTATION_UNLOCK_REFUSED':
      return `Unlock refused for ${self ? actor : subject}${field(row, 'cause') ? ` (${field(row, 'cause')!.replace(/_/g, ' ')})` : ''}`;
    case 'FACE_ENROLLED':
      return `${self ? actor : subject} enrolled their face for workstation unlock`;
    case 'LOAN_REVIEW_SHIFT_STARTED':
      return `${self ? actor : subject} clocked on for loan review`;
    case 'LOAN_REVIEW_SHIFT_ENDED':
      return `${self ? actor : subject} clocked off`;
    case 'LOAN_REVIEW_SHIFT_ENDED_HOLDING':
      return `${reviewer ?? actor} clocked off while holding ${subject}`;
    case 'LOAN_REVIEW_OVERDUE':
      return `${subject} is overdue with ${reviewer ?? 'its reviewer'}`;
    case 'LOAN_REVIEW_DECIDED':
      return `${actor} decided ${subject}`;
    case 'CREATE_ADMIN_USER':
      return `${actor} created ${userNoun(subject)}`;
    case 'UPDATE_ADMIN_USER':
      return `${actor} updated ${target}`;
    case 'DELETE_ADMIN_USER':
      return `${actor} deleted ${userNoun(subject)}`;
    case 'RESET_ADMIN_PASSWORD':
      return `${actor} reset the password for ${target}`;
    case 'PASSKEY_ENROLMENT_TOKEN_ISSUED':
      return `${actor} issued a passkey enrolment code for ${subject}`;
    case 'IAM_PERMISSION_SYNCED':
      return `Permission "${subject}" synced from the catalog`;
    case 'IAM_ASSIGNMENT_EXPIRED':
      return `A role grant expired for ${subject}`;
    default: {
      const meta = auditActionMeta(row.action);
      return meta.verb ? `${actor} ${meta.verb} ${subject}` : `${actor}: ${meta.label.toLowerCase()} — ${subject}`;
    }
  }
}

/** Short device description from a user agent ("Chrome on Windows"); the raw string stays in the details. */
export function describeUserAgent(ua: string | null | undefined): string | null {
  if (!ua) return null;
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\//.test(ua)
      ? 'Opera'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Firefox\//.test(ua)
          ? 'Firefox'
          : /Safari\//.test(ua)
            ? 'Safari'
            : null;
  const os = /Windows/.test(ua)
    ? 'Windows'
    : /Android/.test(ua)
      ? 'Android'
      : /iPhone|iPad/.test(ua)
        ? 'iOS'
        : /Mac OS X/.test(ua)
          ? 'macOS'
          : /Linux/.test(ua)
            ? 'Linux'
            : null;
  if (browser && os) return `${browser} on ${os}`;
  return browser ?? os ?? (ua.length > 40 ? `${ua.slice(0, 40)}…` : ua);
}

/** "Today", "Yesterday", else a medium date — the day separator in the table. */
export function auditDayLabel(iso: string | null, now: Date = new Date()): string {
  if (!iso) return 'Unknown date';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown date';
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((day(now) - day(d)) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

/** Today: "just now", "5 min ago", "3 h ago". Earlier days (under their day heading): the clock time. */
export function auditRelativeTime(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const s = Math.round((now.getTime() - t) / 1000);
  if (auditDayLabel(iso, now) === 'Today' && s >= 0) {
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)} min ago`;
    return `${Math.floor(s / 3600)} h ago`;
  }
  return new Date(t).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
